import { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Flag, MapPin, Shield, Trophy, User, UserRound } from 'lucide-react';

const TYPE_ICONS = { player: User, coach: UserRound, referee: Flag, team: Shield, league: Trophy, venue: MapPin };

const baseStyle = {
  cursor: 'pointer',
  color: '#ffffff',
  background: 'rgb(0,220,73)',
  borderRadius: '5px',
  padding: '0 4px',
};

const iconStyle = { display: 'inline-flex', alignItems: 'center', marginLeft: '2px', verticalAlign: 'text-bottom' };

const popoverStyle = {
  position: 'fixed', width: '260px', display: 'flex', flexDirection: 'column', gap: '4px',
  background: '#1a1a1a', border: '1px solid rgba(157,157,157,0.3)', borderRadius: '12px',
  padding: '10px 12px', boxShadow: '0 12px 32px rgba(0,0,0,0.5)', zIndex: 9999, textAlign: 'left',
};

const TEAM_LABELS = ['team', 'current team', 'club'];
const DOB_LABELS = ['date of birth', 'date_of_birth'];
const RAW_DOB_KEYS = ['date_of_birth', 'birth_date', 'birthdate', 'birthday', 'dob'];

function cleanDobValue(value) {
  const str = String(value ?? '');
  const dateMatch = str.match(/^\d{4}-\d{2}-\d{2}/);
  return dateMatch ? dateMatch[0] : str;
}

function pickFacts(entity) {
  const details = Array.isArray(entity?.details) ? entity.details : [];
  const valid = details.filter(d => d && d.label != null && d.value != null && d.value !== '');
  const byLabel = sets => valid.find(d => sets.has(String(d.label).trim().toLowerCase()));
  const team = byLabel(new Set(TEAM_LABELS));
  let dob = byLabel(new Set(DOB_LABELS));
  if (!dob) dob = valid.find(d => /date of birth|\bbirth\b|\bborn\b/i.test(String(d.label)));
  if (!dob) {
    const raw = entity?.raw;
    const rawKey = RAW_DOB_KEYS.find(key => raw?.[key] != null && raw[key] !== '');
    if (rawKey) dob = { key: 'date-of-birth', label: 'Date of birth', value: raw[rawKey] };
  }
  if (dob) dob = { ...dob, value: cleanDobValue(dob.value) };
  return [team, dob].filter(Boolean);
}

const EntityHighlight = ({ entity, onOpen, children }) => {
  const anchorRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [tip, setTip] = useState(null);

  const Icon = TYPE_ICONS[entity?.type] || User;

  const handleEnter = () => {
    setHovered(true);
    if (!entity || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const top = Math.min(rect.bottom + 8, window.innerHeight - 120);
    const left = Math.max(130, Math.min(rect.left + rect.width / 2, window.innerWidth - 130));
    setTip({ top, left });
  };

  return (
    <span
      ref={anchorRef}
      style={baseStyle}
      onMouseEnter={handleEnter}
      onMouseLeave={() => { setHovered(false); setTip(null); }}
      onClick={() => { if (entity) onOpen?.(entity); }}>
      {children}
      <span style={{ ...iconStyle, filter: hovered ? 'drop-shadow(0 0 6px rgba(0,220,73,0.8))' : 'none', transition: 'filter 0.15s ease' }}>
        <Icon size="0.8em" strokeWidth={2.5} />
      </span>
      <AnimatePresence>
        {tip && entity && (
          <motion.span
            initial={{ opacity: 0, y: 6, scale: 0.96, x: '-50%' }}
            animate={{ opacity: 1, y: 0, scale: 1, x: '-50%' }}
            exit={{ opacity: 0, y: 6, scale: 0.96, x: '-50%' }}
            transition={{ duration: 0.15 }}
            style={{ ...popoverStyle, top: tip.top, left: tip.left }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {entity.name}
            </span>
            {pickFacts(entity).map(detail => (
              <span key={detail.key} style={{ fontSize: 12, color: 'var(--mute)', lineHeight: 1.35, wordBreak: 'break-word' }}>
                <span style={{ color: 'rgba(157,157,157,0.9)' }}>{detail.label}: </span>
                {detail.value}
              </span>
            ))}
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
};

export default EntityHighlight;
